import React, { useRef, useState } from "react";
import { toast } from "sonner";
import { Upload } from "lucide-react";
import api from "../lib/api";

const MAX_MB = 200;
const ACCEPT = "video/mp4,video/quicktime,video/webm,video/x-matroska,video/x-msvideo";

/** "Upload video" — streams the user's own file to POST /social/posts/{id}/video. */
export default function UploadVideoButton({ post, onUploaded, label = "Upload video", disabled }) {
  const ref = useRef(null);
  const [progress, setProgress] = useState(null);
  const [error, setError] = useState("");

  const onPick = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setError("");
    if (file.size > MAX_MB * 1024 * 1024) {
      setError(`Videos up to ${MAX_MB} MB. That file is ${(file.size / 1048576).toFixed(0)} MB.`);
      return;
    }
    const fd = new FormData();
    fd.append("file", file);
    setProgress(0);
    try {
      const { data } = await api.post(`/social/posts/${post.id}/video`, fd, {
        timeout: 0,
        onUploadProgress: (ev) => ev.total && setProgress(Math.round((ev.loaded / ev.total) * 100)),
      });
      toast.success("Video attached");
      onUploaded?.(data);
    } catch (err) {
      const d = err?.response?.data?.detail;
      setError(typeof d === "string" ? d : err?.response?.status === 413 ? "That file is too large for the server to accept. Try a shorter or compressed video." : "Upload failed — try again.");
    } finally { setProgress(null); }
  };

  const uploading = progress !== null;
  return (
    <span className="inline-flex flex-col gap-1" data-testid={`upload-video-wrap-${post.id}`}>
      <input ref={ref} type="file" accept={ACCEPT} className="hidden" onChange={onPick} data-testid={`upload-video-input-${post.id}`} />
      <button
        type="button"
        onClick={() => ref.current?.click()}
        disabled={disabled || uploading}
        className="sp-btn sp-btn-ghost text-xs disabled:opacity-50"
        data-testid={`upload-video-${post.id}`}
        title={`Attach your own video (MP4, MOV, WEBM, MKV, AVI — up to ${MAX_MB} MB)`}
      >
        <Upload className="w-3.5 h-3.5" /> {uploading ? `Uploading ${progress}%` : label}
      </button>
      {uploading && (
        <span className="block h-1 w-full rounded-full bg-white/10 overflow-hidden" data-testid={`upload-video-progress-${post.id}`}>
          <span className="block h-full bg-orange-500 transition-[width] duration-200" style={{ width: `${progress}%` }} />
        </span>
      )}
      {error && <span className="text-[11px] text-red-300 max-w-[260px]" data-testid={`upload-video-error-${post.id}`}>{error}</span>}
    </span>
  );
}
